import { useMemo } from 'react';
import { ReactFlow, Background, Controls, MiniMap, Position, type Node, type Edge } from '@xyflow/react';
import dagre from 'dagre';
import { FootprintCluster, FootprintReverseWhois } from '../types';
import '@xyflow/react/dist/style.css';
import { useTheme } from '../lib/theme';

// Color per signal/node kind.
const SIGNAL_COLOR: Record<string, { bg: string; border: string }> = {
  registrant_name: { bg: '#fee2e2', border: '#dc2626' },
  registrant_org: { bg: '#fee2e2', border: '#dc2626' },
  registrant_email: { bg: '#fef3c7', border: '#d97706' },
  phone: { bg: '#ccfbf1', border: '#0d9488' },
  address: { bg: '#ccfbf1', border: '#0d9488' },
  nameserver: { bg: '#ede9fe', border: '#7c3aed' },
};
const EXTERNAL_PER_IDENTITY = 12;
const MAX_NODES = 500;

const N_WIDTH = 200;
const N_HEIGHT = 36;

function truncate(s: string, n = 28): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function buildGraph(clusters: FootprintCluster[], reverse: FootprintReverseWhois[]) {
  const nodes = new Map<string, Node>();
  const edges = new Map<string, Edge>();

  const addNode = (id: string, node: Omit<Node, 'id' | 'position'>) => {
    if (nodes.has(id) || nodes.size >= MAX_NODES) return nodes.has(id);
    nodes.set(id, { id, position: { x: 0, y: 0 }, sourcePosition: Position.Right, targetPosition: Position.Left, ...node });
    return true;
  };
  const addEdge = (source: string, target: string, opts: Partial<Edge> = {}) => {
    if (!nodes.has(source) || !nodes.has(target)) return;
    const id = `${source}->${target}`;
    if (!edges.has(id)) edges.set(id, { id, source, target, type: 'straight', ...opts });
  };

  const ownedNode = (name: string) => {
    const id = `dom:${name}`;
    addNode(id, { data: { label: truncate(name) }, style: { background: '#dbeafe', border: '2px solid #2563eb', borderRadius: 6, fontSize: 11, padding: '4px 8px', width: N_WIDTH } });
    return id;
  };
  const externalNode = (name: string) => {
    const id = `ext:${name}`;
    addNode(id, { data: { label: truncate(name) }, style: { background: '#f8fafc', border: '1px dashed #94a3b8', borderRadius: 6, fontSize: 11, padding: '4px 8px', color: '#64748b', width: N_WIDTH } });
    return id;
  };

  // Layer A — shared-infrastructure clusters (skip registrar; weak signal).
  for (const c of clusters) {
    if (c.is_generic || c.signal_type === 'registrar') continue;
    const sigId = `sig:${c.signal_type}:${c.signal_value}`;
    const color = SIGNAL_COLOR[c.signal_type] ?? { bg: '#f1f5f9', border: '#64748b' };
    addNode(sigId, {
      data: { label: `${c.signal_type.replace(/_/g, ' ')}\n${truncate(c.signal_value)}` },
      style: { background: color.bg, border: `2px solid ${color.border}`, borderRadius: 8, fontSize: 11, fontWeight: 600, padding: '6px 10px', width: N_WIDTH, whiteSpace: 'pre-line', textAlign: 'center' },
    });
    for (const d of c.domains) addEdge(sigId, ownedNode(d), { style: { stroke: color.border, strokeWidth: c.signal_type === 'nameserver' ? 2 : 1.2 } });
  }

  // Layer B — reverse-WHOIS chains: identity → external + owned domains.
  for (const r of reverse) {
    const idnId = `idn:${r.query_type}:${r.query_value}`;
    addNode(idnId, {
      data: { label: `${r.query_type}\n${truncate(r.query_value)}` },
      style: { background: '#fef2f2', border: '2px solid #b91c1c', borderRadius: 8, fontSize: 11, fontWeight: 700, padding: '6px 10px', width: N_WIDTH, whiteSpace: 'pre-line', textAlign: 'center' },
    });
    const ownedSet = new Set(r.owned_domains_found.map((d) => d.toLowerCase()));
    for (const d of r.owned_domains_found) addEdge(idnId, ownedNode(d), { style: { stroke: '#2563eb', strokeWidth: 1.5 } });
    const external = r.connected_domains.filter((d) => !ownedSet.has(d.toLowerCase()));
    for (const d of external.slice(0, EXTERNAL_PER_IDENTITY)) addEdge(idnId, externalNode(d), { style: { stroke: '#cbd5e1', strokeDasharray: '4 3' } });
  }

  // dagre layout (left→right).
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 18, ranksep: 110 });
  for (const n of nodes.values()) g.setNode(n.id, { width: N_WIDTH, height: N_HEIGHT });
  for (const e of edges.values()) g.setEdge(e.source, e.target);
  dagre.layout(g);
  const laidOut = [...nodes.values()].map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: p.x - N_WIDTH / 2, y: p.y - N_HEIGHT / 2 } };
  });

  return { nodes: laidOut, edges: [...edges.values()], truncated: nodes.size >= MAX_NODES };
}

export default function FootprintGraph({ clusters, reverse }: { clusters: FootprintCluster[]; reverse: FootprintReverseWhois[] }) {
  const [, isDark] = useTheme();
  const { nodes, edges, truncated } = useMemo(() => buildGraph(clusters, reverse), [clusters, reverse]);

  if (nodes.length === 0) {
    return <div className="card p-8 text-center text-slate-400 text-sm">No infrastructure links to graph. Run an audit (and "+ Reverse WHOIS" for external chains).</div>;
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Footprint Network</h2>
          <p className="text-xs text-slate-500">Shared signals (NS/identity) linking your domains, plus external domains discovered via reverse-WHOIS.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100 border-2 border-blue-600" /> Owned</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-50 border border-dashed border-slate-400" /> External</span>
        </div>
      </div>
      <div style={{ height: 480 }} className="bg-slate-50">
        <ReactFlow nodes={nodes} edges={edges} fitView minZoom={0.1} nodesDraggable={false} nodesConnectable={false} proOptions={{ hideAttribution: true }} colorMode={isDark ? 'dark' : 'light'}>
          <Background gap={16} size={1} color={isDark ? '#273246' : '#e2e8f0'} />
          <Controls showInteractive={false} />
          <MiniMap zoomable pannable />
        </ReactFlow>
      </div>
      {truncated && <div className="px-6 py-2 text-xs text-amber-600 border-t border-slate-100">Graph capped at {MAX_NODES} nodes — some links omitted for readability.</div>}
    </div>
  );
}
